/**
 * "Ver como" (SIS-438, ADR 0007): o gestor enxerga o Supply como outra pessoa,
 * somente leitura.
 *
 * Quem decide é o banco: `supply.start_view_as` abre uma sessão (30 min) só
 * para gestor real e devolve o id; aqui o id fica guardado POR ABA
 * (sessionStorage) e vai no header `x-supply-view-as` de toda chamada ao
 * PostgREST. Com ele a RLS passa a ser a da pessoa e qualquer escrita é negada.
 * Sessão expirada ou encerrada derruba a requisição — a aba sai do modo.
 *
 * Nenhum token da outra pessoa existe no navegador: continua indo o JWT do
 * gestor. Por isso o que lê com o token da pessoa (PMC, SGI, PC) fica
 * indisponível no modo.
 */

export const VIEW_AS_HEADER = 'x-supply-view-as'
const STORAGE_KEY = 'supply:ver-como'

export interface ViewAs {
  readonly sessionId: string
  readonly targetMemberId: string
  readonly targetUserId: string
  readonly targetName: string
  /** "Gerente · COMPRAS", "Executor · ESTOQUE"... montado ao entrar. */
  readonly targetLabel: string
  readonly expiresAt: string
}

export const VIEW_AS_READ_ONLY = 'Modo visualização: somente leitura.'
export const VIEW_AS_UNAVAILABLE = 'Indisponível no modo visualização.'
/** O banco recusou a sessão (expirou/encerrou): a aba sai do modo. */
export const VIEW_AS_EXPIRED_EVENT = 'supply:ver-como-expirou'

function isViewAs(value: unknown): value is ViewAs {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return ['sessionId', 'targetMemberId', 'targetUserId', 'targetName', 'expiresAt'].every((k) => typeof v[k] === 'string')
}

export function getViewAs(): ViewAs | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    return isViewAs(parsed) ? { ...parsed, targetLabel: parsed.targetLabel ?? '' } : null
  } catch {
    return null
  }
}

export function isViewingAs(): boolean {
  return getViewAs() !== null
}

export function saveViewAs(value: ViewAs) {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
}

export function clearViewAs() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* nada a limpar */
  }
}

/** Milissegundos até expirar (0 = já expirou). */
export function viewAsRemainingMs(value: ViewAs, now = Date.now()): number {
  return Math.max(0, new Date(value.expiresAt).getTime() - now)
}

/** Só as chamadas ao PostgREST levam o header (GoTrue e Realtime não). */
export function withViewAsHeader(baseFetch: typeof fetch = fetch): typeof fetch {
  return (input, init) => {
    const viewAs = getViewAs()
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    if (!viewAs || !url.includes('/rest/v1/')) return baseFetch(input, init)
    const headers = new Headers(input instanceof Request ? input.headers : undefined)
    new Headers(init?.headers).forEach((value, key) => headers.set(key, value))
    headers.set(VIEW_AS_HEADER, viewAs.sessionId)
    return baseFetch(input, { ...init, headers })
  }
}

/** Erro do banco que diz "a sessão de visualização não vale mais". */
export function isViewAsSessionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const hint = (error as { hint?: unknown }).hint
  return hint === 'ver_como_expirado' || hint === 'ver_como_invalido'
}
